import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner, Notice } from '@wordpress/components';

const ScanHistoryTab = () => {
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_scan_history',
                    nonce: mtrimAdminData.nonce,
                    limit: 50,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setHistory(data.data);
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to load scan history', 'media-trim') });
        }
        setLoading(false);
    };

    const getScanTypeIcon = (scanType) => {
        const icons = {
            unused: 'media-code',
            duplicate: 'images-alt2',
            filesystem: 'media-document',
        };
        return icons[scanType] || 'search';
    };

    const getScanTypeColor = (scanType) => {
        const colors = {
            unused: '#2271b1',
            duplicate: '#d63638',
            filesystem: '#00a32a',
        };
        return colors[scanType] || '#787c82';
    };

    return (
        <div className="mtrim-scan-history-tab">
            <div className="mtrim-tab-header">
                <h2>{__('Scan History', 'media-trim')}</h2>
                <p className="mtrim-description">
                    {__('View your previous scans and their results.', 'media-trim')}
                </p>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                >
                    {notice.message}
                </Notice>
            )}

            <div className="mtrim-history-actions">
                <Button
                    variant="secondary"
                    onClick={fetchHistory}
                    disabled={loading}
                >
                    {loading ? <Spinner /> : (
                        <>
                            <span className="dashicons dashicons-update"></span>
                            {__('Refresh', 'media-trim')}
                        </>
                    )}
                </Button>
            </div>

            {loading ? (
                <div className="mtrim-loading-container">
                    <Spinner />
                    <p>{__('Loading scan history...', 'media-trim')}</p>
                </div>
            ) : history.length === 0 ? (
                <div className="mtrim-empty-state">
                    <span className="dashicons dashicons-clock"></span>
                    <h3>{__('No Scan History', 'media-trim')}</h3>
                    <p>{__('Your scan history will appear here after you run your first scan.', 'media-trim')}</p>
                </div>
            ) : (
                <div className="mtrim-history-list">
                    <table className="mtrim-history-table">
                        <thead>
                            <tr>
                                <th>{__('Scan Type', 'media-trim')}</th>
                                <th>{__('Date', 'media-trim')}</th>
                                <th>{__('Items Scanned', 'media-trim')}</th>
                                <th>{__('Items Found', 'media-trim')}</th>
                                <th>{__('Duration', 'media-trim')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <span 
                                            className="mtrim-scan-type-badge"
                                            style={{ backgroundColor: getScanTypeColor(item.scan_type) }}
                                        >
                                            <span className={`dashicons dashicons-${getScanTypeIcon(item.scan_type)}`}></span>
                                            {item.scan_type_label}
                                        </span>
                                    </td>
                                    <td>{item.formatted_date}</td>
                                    <td>{item.items_scanned.toLocaleString()}</td>
                                    <td>
                                        <strong>{item.items_found.toLocaleString()}</strong>
                                    </td>
                                    <td>{item.formatted_duration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <style>{`
                .mtrim-history-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                    background: #fff;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                .mtrim-history-table th,
                .mtrim-history-table td {
                    padding: 12px 16px;
                    text-align: left;
                    border-bottom: 1px solid #f0f0f1;
                }
                .mtrim-history-table th {
                    background: #f6f7f7;
                    font-weight: 600;
                    color: #1d2327;
                }
                .mtrim-history-table tbody tr:hover {
                    background: #f9f9f9;
                }
                .mtrim-scan-type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 4px;
                    color: #fff;
                    font-size: 12px;
                    font-weight: 500;
                }
                .mtrim-scan-type-badge .dashicons {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            `}</style>
        </div>
    );
};

export default ScanHistoryTab;
